import React from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import clsx from 'clsx'

const markdownComponents: Components = {
  p: ({ className, ...props }) => (
    <p className={clsx('my-2 leading-relaxed text-base-content/90', className)} {...props} />
  ),
  strong: ({ className, ...props }) => (
    <strong className={clsx('font-semibold text-base-content', className)} {...props} />
  ),
  em: ({ className, ...props }) => <em className={clsx('italic', className)} {...props} />,
  ul: ({ className, ...props }) => (
    <ul className={clsx('my-2 list-disc pl-5 space-y-1', className)} {...props} />
  ),
  ol: ({ className, ...props }) => (
    <ol className={clsx('my-2 list-decimal pl-5 space-y-1', className)} {...props} />
  ),
  li: ({ className, ...props }) => <li className={clsx('pl-0.5', className)} {...props} />,
  h1: ({ className, ...props }) => (
    <h1
      className={clsx('text-xl font-bold text-base-content mt-5 mb-2 first:mt-0', className)}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={clsx('text-lg font-semibold text-base-content mt-4 mb-2 first:mt-0', className)}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={clsx('text-base font-semibold text-base-content mt-3 mb-1.5 first:mt-0', className)}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4 className={clsx('text-sm font-semibold text-base-content mt-3 mb-1 first:mt-0', className)} {...props} />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={clsx('border-l-4 border-primary/50 pl-4 my-3 text-base-content/80 italic', className)}
      {...props}
    />
  ),
  code: ({ className, ...props }) => (
    <code className={clsx('font-mono text-[0.875em] [pre_&]:text-sm [pre_&]:bg-transparent', className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <pre className={clsx('overflow-x-auto rounded-lg bg-base-200 p-3 my-3 text-sm', className)} {...props} />
  ),
  hr: ({ className, ...props }) => <hr className={clsx('border-base-300 my-6', className)} {...props} />,
  table: ({ children, ...props }) => (
    <div className="my-4 overflow-x-auto rounded-lg border border-base-300">
      <table className="table table-sm table-zebra w-full border-collapse" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ className, ...props }) => <thead className={clsx('bg-base-200', className)} {...props} />,
  th: ({ className, ...props }) => (
    <th className={clsx('border border-base-300 px-3 py-2 text-left font-semibold text-sm', className)} {...props} />
  ),
  td: ({ className, ...props }) => (
    <td className={clsx('border border-base-300 px-3 py-2 align-top text-sm', className)} {...props} />
  ),
  a: ({ href, className, ...props }) => (
    <a
      href={href}
      className={clsx('link link-primary underline-offset-2', className)}
      {...(href?.startsWith('http://') || href?.startsWith('https://')
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
      {...props}
    />
  ),
}

export interface MarkdownContentProps {
  markdown: string
  className?: string
}

/** Renders GitHub-flavored Markdown (tables, lists, emphasis, etc.) with theme-aware styling. */
const inlineCodeParents =
  '[&_p>code]:rounded [&_p>code]:bg-base-200 [&_p>code]:px-1.5 [&_p>code]:py-0.5 ' +
  '[&_li>code]:rounded [&_li>code]:bg-base-200 [&_li>code]:px-1.5 [&_li>code]:py-0.5 ' +
  '[&_td>code]:rounded [&_td>code]:bg-base-200 [&_td>code]:px-1 [&_td>code]:py-0.5 ' +
  '[&_th>code]:rounded [&_th>code]:bg-base-200 [&_th>code]:px-1 [&_th>code]:py-0.5 ' +
  '[&_strong>code]:rounded [&_strong>code]:bg-base-200 [&_strong>code]:px-1 [&_strong>code]:py-0.5 ' +
  '[&_em>code]:rounded [&_em>code]:bg-base-200 [&_em>code]:px-1 [&_em>code]:py-0.5'

export const MarkdownContent: React.FC<MarkdownContentProps> = ({ markdown, className }) => {
  return (
    <div className={clsx('markdown-body text-sm', inlineCodeParents, className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {markdown}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownContent
